import firebase from 'firebase/compat/app';
import 'firebase/auth';
import envBase from 'env/.public-env.json';
import captureException from './captureException';

// USE_LOCAL_INSIGHTS is only present in the gitignored local env/.public-env.json. It
// routes the request through the Vite dev server (localhost:3000), whose
// server.proxy forwards it to the local Functions emulator without CORS.
const env = envBase as typeof envBase & { USE_LOCAL_INSIGHTS?: boolean };

// Mirrors functions/src/insights/types.ts ChartFix / ChartCurrencyType.
export type ChartFix =
  | 'currency'
  | 'percentage'
  | 'duration'
  | 'fuelEfficiency'
  | 'distance'
  | 'weight'
  | 'temperature';
export type ChartCurrencyType = 'USD' | 'CAD';

// Mirrors functions/src/insights/types.ts InsightsChartSpec: a fully-resolved
// chart the agent rendered. categories + series come verbatim from an
// aggregate_metric result (no client-side data fetching). The `source` block is
// used only when saving the chart to a dashboard/report (see Part B).
// Mirrors functions/src/insights/types.ts INSIGHTS_CHART_TYPES. There is no
// compiler link between the two, so a contract test asserts they match.
export const INSIGHTS_CHART_TYPES = [
  'bar',
  'column',
  'line',
  'area',
  'pie',
  'donut',
  'radar',
  'waterfall',
  'packedBubble',
  'funnel',
  'pyramid',
  'scatter',
  'bubble',
  'heatmap',
  'sankey',
] as const;

export type InsightsChartType = (typeof INSIGHTS_CHART_TYPES)[number];

export interface InsightsChartSpec {
  chartType: InsightsChartType;
  title?: string;
  // 2b — stack the series instead of grouping them (matrix / comparison where
  // the parts sum to a meaningful whole). Maps to a stacked Highcharts plot
  // inline and a stackedBar/stackedArea gadget when saved.
  stacked?: boolean;
  categoryLabel?: string;
  valueLabel?: string;
  // Names what the SERIES represent, when that isn't the metric — on a cross-tab
  // each series is a member of the first dimension, so this labels the heatmap's
  // y-axis.
  seriesLabel?: string;
  categories: string[];
  // A null datum means NO DATA for that category (missing trend bucket or
  // cross-tab cell) — rendered as a line gap / blank heatmap cell, as distinct
  // from a real measured 0.
  series: { name: string; data: (number | null)[] }[];
  formatting?: {
    prefix?: ChartFix;
    postfix?: ChartFix;
    currencyType?: ChartCurrencyType;
    precision?: number;
  };
  // Members the agent EXCLUDED from this chart to keep it readable (a dominant or
  // blank member, or high-cardinality outliers). Inline-only: surfaced as a
  // warning box above the chart, NOT plotted and NOT used when saving to a live
  // gadget. Values are read verbatim server-side from the original result — the
  // model never types them. `value` is null when the member had no numeric value.
  excludedOutliers?: { label: string; value: number | null }[];
  source: {
    metricId: string;
    metricType: 'normal' | 'compound' | 'special';
    // Extra metrics overlaid for a multi-metric comparison (2a); carried so a
    // saved chart's gadget gets a series per metric.
    compareMetricIds?: {
      metricId: string;
      metricType: 'normal' | 'compound' | 'special';
    }[];
    breakdownField?: string;
    breakdownFieldType?: string;
    // A numeric-distribution (histogram) result: the field bucketed and the
    // RESOLVED bins. Becomes the saved gadget's dimensionA.rangeInput. Mutually
    // exclusive with breakdownField / breakdownField2 / trendByCalendarInterval.
    bucketField?: string;
    bucketResolved?: {
      field: string;
      ranges?: { key: string; from?: number; to?: number }[];
      interval?: number;
    };
    // The SECOND categorical dimension (cross-tab only). Becomes the gadget's
    // dimensionB; mutually exclusive with trendByCalendarInterval.
    breakdownField2?: string;
    breakdownField2Type?: string;
    // The top-N cap the chat applied to each dimension, so the saved gadget
    // shows the same grid the user saw (buildSubGroupBy reads groupByLimit).
    breakdownLimit?: number;
    // Which end of the metric ordering a ranked-list (find_outliers) chart
    // shows — the saved gadget sorts its top-N by this ('asc' = bottom-N).
    // Absent on plain breakdowns (metric-descending default).
    sortDirection?: 'asc' | 'desc';
    // The RESOLVED trend bucket (concrete, never 'auto') — labels the chart.
    trendByCalendarInterval?: 'year' | 'quarter' | 'month' | 'week' | 'day';
    // The model asked for an auto-selected bucket; the saved gadget is rebuilt
    // with 'auto' so it re-buckets by its range like a native gadget.
    trendIntervalAuto?: boolean;
    dateScope: { startDate: string; endDate: string };
    filters?: {
      field: string;
      kind: 'text' | 'number' | 'boolean';
      operator: string;
      values: (string | number | boolean)[];
    }[];
    // The series is a value the chat COMPUTED (a decompose_metric
    // period-over-period delta per member), not a live query — no gadget can
    // reproduce it, so the save path refuses it (see canSaveSpec).
    derived?: 'period-delta';
  };
}

// Mirrors functions/src/insights/types.ts ProposedMetricFormatting — display
// hints carried on a proposed metric (subset of MetricFormatting).
export interface ProposedMetricFormatting {
  prefix?: ChartFix;
  postfix?: ChartFix;
  currencyType?: ChartCurrencyType;
  precision?: number;
}

// Mirrors functions/src/insights/types.ts ProposedNormalMetric.
export interface ProposedNormalMetric {
  name: string;
  description: string;
  dataset: string;
  field: string;
  aggFunc: string;
  formatting?: ProposedMetricFormatting;
}

// Mirrors functions/src/insights/types.ts ProposedCompoundMetric.
export interface ProposedCompoundMetric {
  name: string;
  description: string;
  metricIds: string[];
  expression: string;
  formatting?: ProposedMetricFormatting;
}

// Mirrors functions/src/insights/types.ts ProposedAction: a write the agent
// proposes (Phase 3 / 3b / 3c). The function never writes — the client renders a
// confirm card and commits it only on explicit confirmation. Discriminated on
// `kind`: chart kinds carry render_chart specs; metric kinds carry a definition.
export type ProposedAction =
  | {
      id: string;
      // 'dashboard'/'report' create a new one (named by `title`).
      kind: 'dashboard' | 'report';
      title: string;
      charts: InsightsChartSpec[];
    }
  | {
      id: string;
      // Adds the charts to a dashboard/report the user picks in the chat (3b).
      kind: 'add_to_existing';
      charts: InsightsChartSpec[];
    }
  | {
      id: string;
      // Adds the charts to a workspace the user picks in the chat (3c). `title`
      // is the name of the wrap-in-a-new-dashboard option.
      kind: 'add_to_workspace';
      title?: string;
      charts: InsightsChartSpec[];
    }
  // studioBlock (Studio only): the block to add once the user confirms, with
  // the new metric first
  | {
      id: string;
      kind: 'create_metric';
      metric: ProposedNormalMetric;
      studioBlock?: StudioBlockSpec;
    }
  | {
      id: string;
      kind: 'create_compound_metric';
      metric: ProposedCompoundMetric;
      studioBlock?: StudioBlockSpec;
    };

// Mirrors functions/src/insights/types.ts AnswerSource: the provenance behind a
// quantitative answer — the metric/date/filters of the ACTUAL aggregate_metric
// query, so the "source" footer is authoritative, not model prose.
export interface AnswerSourceFilter {
  field: string;
  operator: string;
  values: (string | number | boolean)[];
}

// Lineage (ENG-695 Tier 1): a metric field + the raw source-system column it
// derives from.
export interface AnswerSourceField {
  field: string;
  sourceColumn?: string;
  connector?: string;
}

export interface AnswerSource {
  metricId: string;
  metricName: string | null;
  metricType: 'normal' | 'compound' | 'special';
  dataset: string | null;
  startDate: string;
  endDate: string;
  // The date field the range filtered/bucketed on (defaults to 'date' — the
  // same default the app's gadgets use).
  dateField?: string;
  breakdownField?: string;
  // The numeric field the metric was bucketed into a distribution over.
  bucketField?: string;
  trendByCalendarInterval?: string;
  filters?: AnswerSourceFilter[];
  // Lineage (ENG-695 Tier 1): resolved source field(s) + column, the compound
  // formula, and the data "as of" timestamp (newest record — a freshness proxy).
  fields?: AnswerSourceField[];
  compoundExpression?: string;
  asOf?: string;
  // 1-based citation number (stable across dedupe). The answer prose carries
  // matching [n] markers that render as anchors to this source in the footer.
  citationIndex?: number;
}

// Mirrors functions/src/insights/types.ts InsightsStreamEvent.
// Mirrors functions/src/insights/types.ts InsightsContextUsed: what the server
// put in front of the model for one answer — the KB version, and this account's
// knowledge by title (always-on, fired by a trigger, matched-but-capped).
export interface InsightsContextUsed {
  kbVersion: string | null;
  always: string[];
  triggered: string[];
  nearMissed: string[];
}

export type InsightsStreamEvent =
  | { type: 'tool'; name: string; label?: string }
  // reasoning_delta: a live chunk of the model's thinking (staff-only debug).
  // Only ever sent when the caller is verified staff and requested it, so a
  // customer's stream never carries it.
  | { type: 'reasoning_delta'; text: string }
  // data_call: an underlying data-engine call made inside a model tool (the
  // graphql facade tools / gateway aggregations aggregate_metric delegates to).
  // Staff-only; the client shows it as a sub-step of the tool in the trace.
  | { type: 'data_call'; name: string; args?: string }
  // context_used: the knowledge the server injected before the model ran.
  // Staff-only, and the difference between "the note never reached it" and "it
  // had the note and ignored it".
  | { type: 'context_used'; context: InsightsContextUsed }
  | { type: 'delta'; text: string }
  | { type: 'chart'; spec: InsightsChartSpec }
  | { type: 'proposed_action'; action: ProposedAction }
  | { type: 'studio_edit'; edit: StudioBlockEdit }
  | { type: 'studio_add'; block: StudioBlockAdd }
  | { type: 'studio_add_pending'; pending: StudioBlockPending }
  | { type: 'studio_add_cancel'; toolUseId: string }
  | { type: 'studio_arrange'; arrange: StudioBlockArrange }
  | { type: 'grounding'; source: AnswerSource }
  | { type: 'suggestions'; items: string[] }
  | { type: 'usage'; spentUsd: number; budgetUsd: number | null }
  | { type: 'done'; usedTools: string[] }
  // `reason` is a stable machine category for the failure, so the UI and
  // analytics can branch on it without string-matching the human `message`.
  | { type: 'error'; message: string; reason?: InsightsErrorReason };

// Mirrors functions/src/insights/types.ts InsightsErrorReason.
export type InsightsErrorReason =
  | 'auth' // the data service (MCP) rejected our credentials
  | 'timeout' // the data service timed out
  | 'unavailable' // the data service was unreachable for another reason
  | 'budget' // the request was denied by the account's budget cap
  | 'empty_answer' // the model produced no answer
  | 'internal'; // an unhandled server error

// Mirrors functions/src/insights/types.ts InsightsQueryContext: the scope the
// user is asking within, sent so the agent can ground/scope ambiguous questions.
// The workspace is the OUTER scope (narrows/improves answers via its skill); the
// on-screen entity is the INNER narrowing. Both are optional and independent —
// nothing breaks when neither is set.
export interface InsightsQueryContext {
  workspaceId?: string;
  workspaceName?: string;
  entityType?: 'report' | 'dashboard' | 'workspace';
  // The report/dashboard id. The model reads names; account knowledge triggers
  // key on this, because a name match breaks the moment someone renames it.
  entityId?: string;
  entityName?: string;
  metricNames?: string[];
  // The live on-screen state of the entity — dates, filters, gadget dimensions
  // and the figures actually rendered. Structurally identical to InsightsView in
  // src/contextProviders/InsightsContentProvider/viewTypes.ts (which is where the
  // client-side type lives and is documented); repeated here because this file is
  // the wire contract the server mirrors.
  view?: InsightsQueryView;
  // Set only while the user is in the Studio builder: the canvas's blocks and
  // the selected one in full. Unlocks the agent's edit_studio_block tool.
  studio?: InsightsStudioContext;
}

// Mirrors functions/src/insights/types.ts InsightsStudioContext and friends.
export interface InsightsStudioBlockSummary {
  id: string;
  name?: string;
  visType?: string;
  // What a Table's rows or a Line's lines are, where not metrics
  mode?: string;
  metrics: { id: string; name?: string }[];
  // A Composite block's rows of elements
  rows?: InsightsStudioRowSummary[];
}

// Mirrors InsightsStudioRowSummary
export interface InsightsStudioRowSummary {
  id: string;
  elements: {
    id: string;
    visType?: string;
    // What its rows or lines are, where not its metrics (as a block's)
    mode?: string;
    label?: string;
    metrics: { id: string; name?: string }[];
  }[];
}

export interface InsightsStudioDimensionField {
  field: string;
  label: string;
  // The dataset field type; Studio's own dayOfWeek / month groupings are
  // 'grouping'
  type: string;
}

export interface InsightsStudioContext {
  contentType: 'dashboard' | 'report';
  visTypes: { id: string; label: string }[];
  elementVisTypes: { id: string; label: string }[];
  selectedBlockId?: string;
  selectedElementId?: string;
  blocks: InsightsStudioBlockSummary[];
  // Dashboards only: what a popup can open
  popupTargets?: InsightsStudioPopupTarget[];
  // The account's datasets, for blocks that read one directly
  datasets?: { type: string; label: string }[];
  // Dashboards only: the event names a reminders Table can list
  reminderEvents?: string[];
  // The KPI Lists a KPI list Table can show (none on a template)
  kpiLists?: InsightsStudioKpiList[];
  selectedBlock?: {
    id: string;
    settings: Record<string, unknown>;
    missing?: string;
    dimensionFields: InsightsStudioDimensionField[];
    capabilities: InsightsStudioBlockCapabilities;
  };
}

// Mirrors InsightsStudioKpiList: a saved KPI List (scorecard)
export interface InsightsStudioKpiList {
  id: string;
  title: string;
  cadence: string;
}

// Mirrors InsightsStudioPopupTarget: a report or board a dashboard block (or
// chart element) can open as its popup
export interface InsightsStudioPopupTarget {
  type: 'report' | 'board';
  id: string;
  name: string;
}

// Mirrors InsightsStudioBlockCapabilities: what the selected block takes as
// it is now
export interface InsightsStudioBlockCapabilities {
  isOverTime: boolean;
  canGroup: boolean;
  canSort: boolean;
  canLimit: boolean;
  canForecast: boolean;
  canCompare: boolean;
  takesColours: boolean;
}

// One edit the agent made to a Studio block: a patch of Card keys (nested
// objects merge, null clears), applied the moment it arrives.
export interface StudioBlockEdit {
  editId: string;
  blockId: string;
  summary: string;
  settings: Record<string, unknown>;
  // Set when it's to one of a Composite block's elements
  element?: StudioElementChange;
}

// Mirrors StudioElementChange: an element changed, added (to rowId, a new
// row when newRow), removed or moved (into rowId before beforeElementId, or
// a new row at rowIndex); moveRow moves row rowId to rowIndex
export interface StudioElementChange {
  action: 'edit' | 'add' | 'remove' | 'move' | 'moveRow';
  elementId: string;
  rowId?: string;
  newRow?: boolean;
  beforeElementId?: string;
  rowIndex?: number;
}

// Mirrors StudioBlockSize: small = a quarter of the width by a quarter of a
// screen, medium = half by half, wide = the full width by half a screen
export type StudioBlockSize = 'small' | 'medium' | 'wide';

// Mirrors StudioBlockPlacement: where a new block goes when the user says
// (the top or bottom, or above / below / beside blockId); absent, free space
export interface StudioBlockPlacement {
  position: 'top' | 'bottom' | 'above' | 'below' | 'beside';
  blockId?: string;
}

// Mirrors StudioBlockSpec: a block to add, as add_studio_block takes one
export interface StudioBlockSpec {
  summary: string;
  size: StudioBlockSize;
  settings: Record<string, unknown>;
  placement?: StudioBlockPlacement;
}

// Mirrors StudioBlockArrange: a block on the canvas moved (to a placement,
// optionally at a new size), resized in place, or removed
export interface StudioBlockArrange {
  editId: string;
  blockId: string;
  summary: string;
  action: 'move' | 'resize' | 'remove';
  placement?: StudioBlockPlacement;
  size?: StudioBlockSize;
}

// A block the agent added; blockId is minted server-side so a later edit in
// the same answer can name it
export interface StudioBlockAdd {
  editId: string;
  blockId: string;
  summary: string;
  size: StudioBlockSize;
  settings: Record<string, unknown>;
  placement?: StudioBlockPlacement;
  // The call it came from: it takes the place of that call's placeholder
  // (StudioBlockPending)
  toolUseId?: string;
}

// A block the agent has started writing, shown as a placeholder where it will
// go until its studio_add (same toolUseId), or its studio_add_cancel
export interface StudioBlockPending {
  toolUseId: string;
  summary: string;
  size: StudioBlockSize;
  visType: string;
}

// Mirrors functions/src/insights/types.ts InsightsQueryView and friends.
export interface InsightsQueryViewFilterClause {
  field: string;
  kind: 'text' | 'number' | 'boolean';
  operator: string;
  values: (string | number | boolean)[];
}

export interface InsightsQueryViewFilter {
  description: string;
  // AND-combined clauses (one filter can need two, e.g. exclude-Blanks-plus-
  // values). Empty/absent ⇒ not reproducible.
  clauses?: InsightsQueryViewFilterClause[];
  // Legacy singular mirror, set only for single-clause filters. See viewTypes.
  clause?: InsightsQueryViewFilterClause;
}

export interface InsightsQueryViewValues {
  asOf: string;
  total?: { metric: string; value: number };
  rows?: { label: string; values: { metric: string; value: number }[] }[];
  rowsTruncatedFrom?: number;
}

export interface InsightsQueryViewGadget {
  title: string;
  metrics: { id: string; name?: string }[];
  dimensionA?: { field: string; label: string };
  dimensionB?: { field: string; label: string };
  trend?: string;
  isTrendReproducible?: boolean;
  groupByLimit?: number;
  groupBySort?: string;
  dateFieldOverride?: string;
  extraFilters?: InsightsQueryViewFilter[];
  ignoresReportFilters?: boolean;
  comparison?: string;
  values?: InsightsQueryViewValues;
  valuesUnavailableReason?: 'unsupported-gadget' | 'loading' | 'truncated';
  // A dashboard gadget's absolute resolved scope (dashboards have no view-level
  // scope). Reports never set it.
  ownScope?: {
    startDate?: string;
    endDate?: string;
    dateField?: string;
    weekStartsOn?: string;
    comparison?: string;
    filters?: InsightsQueryViewFilter[];
  };
  // Where a click on a value in this gadget navigates (dashboard gadgets only).
  drillTarget?: InsightsQueryDrillTarget;
  // Series that ask their own question (V5SeriesQueryOverrides)
  seriesOverrides?: {
    metric: { id: string; name?: string };
    label: string;
    ignoresFilters?: boolean;
    filters?: InsightsQueryViewFilter[];
    period?: string;
    dateField?: string;
  }[];
}

export interface InsightsQueryDrillTarget {
  type: 'report' | 'board';
  id: string;
  name?: string;
}

// The single on-screen value the user asked about ("Explain this value").
export interface InsightsQueryViewFocus {
  gadgetTitle: string;
  metrics: { id: string; name?: string }[];
  dimension?: { field: string; label: string };
  value?: {
    label: string;
    rawValue: string | number | boolean;
    clause?: InsightsQueryViewFilter['clause'];
  };
  dateBucket?: { startDate: string; endDate: string; label?: string };
  onScreenValues?: { metric: string; value: number }[];
  drillTarget?: InsightsQueryDrillTarget;
}

export interface InsightsQueryView {
  startDate?: string;
  endDate?: string;
  dateField?: string;
  comparison?: string;
  filters: InsightsQueryViewFilter[];
  filtersTruncatedFrom?: number;
  hasUnreproducibleFilters: boolean;
  gadgets: InsightsQueryViewGadget[];
  gadgetsTruncatedFrom?: number;
  weekStartsOn?: string;
  focus?: InsightsQueryViewFocus;
}

const STREAM_PATH = '/v1/content/insightsQueryStream';

// Same agent, same frames, but run against an UNPUBLISHED knowledge-base draft
// (Agent Management "try it"). Staff-only server-side.
export const AGENT_KB_TRY_IT_PATH = '/v1/content/insightsAgentKbTryIt';

// POST the question and consume the Server-Sent Events response, invoking
// onEvent for each frame. apisauce/axios can't stream a response body cleanly,
// so we assemble the request by hand: the firebase id token for auth, the
// account id header, and the same base-URL rule as buildCloudFunctionClient.ts.
const streamInsightsQuery = async ({
  accountId,
  userId,
  budgetUsd,
  question,
  history,
  context,
  conversationId,
  answerId,
  debug,
  todayOverride,
  onEvent,
  signal,
  path = STREAM_PATH,
}: {
  accountId: string;
  // Identity + effective budget for per-user usage metering (1b). budgetUsd is
  // null for unlimited (staff).
  userId?: string;
  budgetUsd?: number | null;
  question: string;
  history: { role: string; content: string }[];
  context?: InsightsQueryContext;
  // Active conversation id, forwarded so the server-side query log can correlate
  // to the persisted chat (staff analytics). Optional.
  conversationId?: string;
  // The pending assistant message's id, forwarded so the server-side query log
  // stores it (answerId) — lets later feedback keyed to this message resolve to
  // the answer's server context. Optional.
  answerId?: string;
  // Staff-only: request the reasoning trace. The server re-verifies staff before
  // honouring it (see handleInsightsQueryStream), so this is safe to send.
  debug?: boolean;
  // The account's "today" override (Advanced settings), so relative dates in
  // the answer match the rest of the app. The server prefers a demo account's
  // stored value; this carries the per-session one on other accounts.
  todayOverride?: string;
  onEvent: (event: InsightsStreamEvent) => void;
  signal?: AbortSignal;
  // Which streaming endpoint to hit. Defaults to the live agent; the KB editor
  // points it at the draft "try it" endpoint, which is otherwise identical.
  path?: string;
}): Promise<void> => {
  const baseURL = env.USE_LOCAL_INSIGHTS
    ? 'http://localhost:3000'
    : env.API_GATEWAY_URL;

  const token = await firebase.auth().currentUser?.getIdToken();

  const response = await fetch(`${baseURL}${path}`, {
    method: 'POST',
    headers: {
      accept: 'text/event-stream',
      'content-type': 'application/json',
      authorization: `Bearer ${token ?? ''}`,
      'x-account-id': accountId,
      'x-user-id': userId ?? '',
      'x-ai-budget-usd':
        budgetUsd === undefined || budgetUsd === null ? '' : String(budgetUsd),
    },
    body: JSON.stringify({
      question,
      history,
      context,
      conversationId,
      answerId,
      ...(debug ? { debug: true } : {}),
      ...(todayOverride ? { todayOverride } : {}),
    }),
    signal,
  });

  if (!response.ok || !response.body) {
    onEvent({
      type: 'error',
      message: `Insights request failed (${response.status}).`,
    });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // The server ends every run with a terminal `done` or `error` frame. Track it
  // so a 200 stream that closes WITHOUT one (a dropped or degenerate run) can be
  // surfaced as an error rather than leaving the caller's pending state spinning
  // forever (the exact silent-hang failure mode from the ENG-766 preview).
  let sawTerminalEvent = false;

  // Emit each complete SSE frame (frames are separated by a blank line).
  const flushFrames = () => {
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const dataLine = frame
        .split('\n')
        .find((line) => line.startsWith('data:'));
      if (dataLine) {
        const payload = dataLine.slice('data:'.length).trim();
        if (payload) {
          try {
            const event = JSON.parse(payload) as InsightsStreamEvent;
            if (event.type === 'done' || event.type === 'error') {
              sawTerminalEvent = true;
            }
            onEvent(event);
          } catch (error) {
            // A frame we can't parse (e.g. a malformed terminal frame) would
            // otherwise vanish silently and hang the caller — report it so the
            // bad frame is diagnosable rather than dropped without a trace.
            captureException(
              error instanceof Error ? error : new Error(String(error)),
            );
          }
        }
      }
      boundary = buffer.indexOf('\n\n');
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    flushFrames();
  }

  // A clean 200 that closed without a terminal frame is still a failed answer:
  // synthesize an error so the caller resolves its pending state instead of
  // spinning indefinitely. (Aborts reject out of the read loop above and never
  // reach here, so a user-cancelled request won't be reported as an error.)
  if (!sawTerminalEvent) {
    onEvent({
      type: 'error',
      message: 'The response ended unexpectedly. Please try again.',
    });
  }
};

export default streamInsightsQuery;
